import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';

// Error handling solution: error types
export enum ErrorShowType {
    SILENT = 0,
    WARN_MESSAGE = 1,
    ERROR_MESSAGE = 2,
    NOTIFICATION = 3,
    REDIRECT = 9,
}

// Response data format agreed with backend
interface ResponseStructure {
    success: boolean;
    data: any;
    code?: number;      // Changed from errorCode
    message?: string;   // Changed from errorMessage
    showType?: ErrorShowType;
}

/**
 * @name Error handling
 * Pro's built-in error handling, you can make your own changes here
 * @doc https://umijs.org/docs/max/request#configuration
 */
export const errorConfig: RequestConfig = {
    // Error handling: umi@3 error handling solution.
    errorConfig: {
        // Error throwing
        errorThrower: (res: any) => {
            const { success, data, code, message: msg, showType } =
                res as unknown as ResponseStructure;
            if (success === false) {
                const error: any = new Error(msg);
                error.name = 'BizError';
                error.info = { code, message: msg, showType, data };
                throw error; // Throw homemade error
            }
        },
        // Error reception and handling
        errorHandler: (error: any, opts: any) => {
            if (opts?.skipErrorHandler) throw error;
            // Our errorThrower threw an error.
            if (error.name === 'BizError') {
                const errorInfo: ResponseStructure | undefined = error.info;
                if (errorInfo) {
                    const { message: msg, code } = errorInfo;
                    switch (errorInfo.showType) {
                        case ErrorShowType.SILENT:
                            // do nothing
                            break;
                        case ErrorShowType.WARN_MESSAGE:
                            message.warning(msg);
                            break;
                        case ErrorShowType.ERROR_MESSAGE:
                            message.error(msg);
                            break;
                        case ErrorShowType.NOTIFICATION:
                            notification.open({
                                description: msg,
                                message: code,
                            });
                            break;
                        case ErrorShowType.REDIRECT:
                            // TODO: redirect
                            break;
                        default:
                            message.error(msg);
                    }
                }
            } else if (error.response) {
                // Axios Error
                // requests successfully header
                const { data } = error.response as unknown as ResponseStructure;
                const errorMsg = data?.message || data?.detail || 'Request error, please retry.';
                // Safely handle object/array messages (e.g., FastAPI validation errors) to prevent React #31 crash
                const finalMsg = typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg);
                message.error(finalMsg);
            } else if (error.request) {
                // Request successfully header
                message.error('None response! Please retry.');
            } else {
                // Send request error
                message.error('Request error, please retry.');
            }
        },
    },

    // Request interceptors
    requestInterceptors: [
        (config: RequestOptions) => {
            // Intercept request configuration for personalized processing.
            // Note: Removed the token=123 addition as it interferes with proper Bearer token auth
            return { ...config };
        },
    ],

    // Response interceptors
    responseInterceptors: [
        (response) => {
            // Intercept response data for personalized processing
            const { data } = response as unknown as ResponseStructure;

            if (data?.success === false) {
                message.error('Request failed!');
            }
            return response;
        },
    ],
};
